import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from '@/hooks/use-toast';
import { DeviceForm } from './device-form';
import { useGetLocationsQuery, usePostDevicesMutation, usePatchDevicesByIdMutation } from '@/services';

interface DeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  device?: any; // For edit mode
  mode: 'create' | 'edit';
}

export function DeviceModal({ isOpen, onClose, onSuccess, device, mode }: DeviceModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: locations, isLoading: isLoadingLocations } = useGetLocationsQuery({});
  const [createDevice] = usePostDevicesMutation();
  const [updateDevice] = usePatchDevicesByIdMutation();

  const handleSubmit = async (formData: any) => {
    setIsSubmitting(true);
    try {
      if (mode === 'edit' && device) {
        await updateDevice({
          id: device.id,
          body: formData,
        }).unwrap();
        toast({
          title: 'Success',
          description: 'Device updated successfully',
        });
      } else {
        await createDevice({ body: formData }).unwrap();
        toast({
          title: 'Success',
          description: 'Device created successfully',
        });
      }
      onSuccess?.();
      onClose();
    } catch (_error) {
      toast({
        variant: 'error',
        title: 'Error',
        description: `Failed to ${mode} device`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (isLoadingLocations) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <div className="flex items-center justify-center p-6">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const initialData =
    mode === 'edit' && device
      ? {
          name: device.name || '',
          description: device.description || '',
          serial_number: device.serial_number || '',
          mac_address: device.mac_address || '',
          location_id: device.location_id || '',
        }
      : {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader className="sr-only">
          <div>Device {mode === 'edit' ? 'Edit' : 'Create'}</div>
        </DialogHeader>
        <ScrollArea className="max-h-[calc(90vh-2rem)] pr-6">
          <DeviceForm
            initialData={initialData}
            locations={
              locations?.data?.map((loc) => ({
                id: loc.id,
                name: loc.name,
                description: loc.description || undefined,
              })) || []
            }
            isLoading={isSubmitting}
            isEdit={mode === 'edit'}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
          />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
